import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Heart, Wrench, LayoutGrid, User, Package, LogOut, ChevronDown } from "lucide-react";
import { SearchBar } from "@/components/SearchBar";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";

const LOGO_URL = "https://www.gotparts.ca/wp-content/uploads/2024/12/logo.png";

export const Navbar = () => {
  const { cartCount, favourites, techMode, setTechMode } = useStore();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isCustomer = user && user.role === "customer";

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-4 h-16">
          <Link to="/" data-testid="logo-link" className="flex items-center shrink-0">
            <img src={LOGO_URL} alt="GOT PARTS" className="h-10 w-auto object-contain" />
          </Link>

          <div className="hidden md:block flex-1 max-w-2xl mx-auto">
            <SearchBar />
          </div>

          <nav className="flex items-center gap-1 sm:gap-2 shrink-0">
            <Link to="/catalogue" data-testid="nav-catalogue" className="hidden sm:flex items-center gap-1.5 text-sm font-medium text-steel hover:text-graphite px-2 py-2">
              <LayoutGrid className="w-4 h-4" /> Catalogue
            </Link>
            <Link to="/tech" data-testid="nav-tech" className="hidden sm:flex items-center gap-1.5 text-sm font-medium text-steel hover:text-graphite px-2 py-2">
              <Wrench className="w-4 h-4" /> Tech Portal
            </Link>
            <div className="hidden lg:flex items-center gap-2 px-2">
              <Wrench className="w-4 h-4 text-steel-light" />
              <span className="text-xs font-medium text-steel">Tech</span>
              <Switch data-testid="tech-mode-toggle" checked={techMode} onCheckedChange={setTechMode} />
            </div>
            <button data-testid="nav-favourites" onClick={() => navigate("/favourites")} className="relative p-2 text-steel hover:text-graphite">
              <Heart className="w-5 h-5" />
              {favourites.length > 0 && <span className="absolute -top-0.5 -right-0.5 bg-graphite text-white text-[10px] mono rounded-full w-4 h-4 grid place-items-center">{favourites.length}</span>}
            </button>
            <button data-testid="nav-cart" onClick={() => navigate("/cart")} className="relative p-2 text-steel hover:text-graphite">
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && <span className="absolute -top-0.5 -right-0.5 bg-orange text-white text-[10px] mono rounded-full w-4 h-4 grid place-items-center">{cartCount}</span>}
            </button>

            {isCustomer ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="account-menu" className="flex items-center gap-1.5 pl-2 pr-1 sm:pr-2 py-1.5 rounded-md hover:bg-surface">
                    <div className="w-7 h-7 rounded-full bg-orange/15 text-orange grid place-items-center font-semibold text-sm">{(user.name || "U").charAt(0).toUpperCase()}</div>
                    <span className="hidden sm:block text-sm font-medium text-graphite max-w-[90px] truncate">{user.name?.split(" ")[0]}</span>
                    <ChevronDown className="w-3.5 h-3.5 text-steel-light hidden sm:block" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuLabel className="truncate">{user.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-account" onClick={() => navigate("/account")}><User className="w-4 h-4 mr-2" /> My Account</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-orders" onClick={() => navigate("/account?tab=orders")}><Package className="w-4 h-4 mr-2" /> My Orders</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); navigate("/"); }}><LogOut className="w-4 h-4 mr-2" /> Logout</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" data-testid="nav-signin" className="ml-1 inline-flex items-center gap-1.5 bg-graphite hover:bg-orange text-white text-sm font-medium rounded-md px-3 h-9 transition-colors">
                <User className="w-4 h-4" /> <span className="hidden sm:inline">Sign in</span>
              </Link>
            )}
          </nav>
        </div>

        <div className="md:hidden pb-3">
          <SearchBar />
        </div>
      </div>
    </header>
  );
};
